import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { CheckCircle, User, Mail, Lock, Phone, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthorOrderConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [packageInfo, setPackageInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: ''
  });

  useEffect(() => {
    // Get package info from sessionStorage or URL params
    const storedPackage = sessionStorage.getItem('selected_author_package');
    if (storedPackage) {
      setPackageInfo(JSON.parse(storedPackage));
    } else {
      // Try URL params as fallback
      const packageId = searchParams.get('package');
      if (packageId) {
        setPackageInfo({
          id: packageId,
          title: getPackageTitle(packageId),
          price: getPackagePrice(packageId)
        });
      }
    }
  }, [searchParams]);

  const getPackageTitle = (id) => {
    const titles = {
      '2_books': 'Video Book Review - Up to 2 Books',
      '5_books': 'Video Book Review - Up to 5 Books',
      '9_books': 'Video Book Review - Up to 9 Books',
      'trailer': 'Video Promo Book Trailer',
      'excerpt': 'Video Book Excerpts'
    };
    return titles[id] || id;
  };

  const getPackagePrice = (id) => {
    const prices = {
      '2_books': 55,
      '5_books': 180,
      '9_books': 220,
      'trailer': 60,
      'excerpt': 99
    };
    return prices[id] || 0;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!packageInfo) {
      toast.error('No package selected. Please go back and select a package.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/author/register`, {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        package_id: packageInfo.id
      });

      // Store author info in localStorage
      localStorage.setItem('author_id', response.data.author_id);
      localStorage.setItem('author_email', response.data.email);
      localStorage.setItem('author_order_id', response.data.order_id);
      
      // Clear sessionStorage
      sessionStorage.removeItem('selected_author_package');

      toast.success('Account created successfully!');
      
      // Navigate to author dashboard
      navigate('/author-dashboard');
      
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-red-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Payment Received!</h1>
          <p className="text-slate-600">Thank you for your order. Create your account to continue.</p>
        </div>

        {/* Package Info */}
        {packageInfo && (
          <Card className="p-6 mb-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm mb-1">Your Order</p>
                <h3 className="text-xl font-bold">{packageInfo.title}</h3>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm mb-1">Amount Paid</p>
                <p className="text-2xl font-bold">£{packageInfo.price}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Registration Form */}
        <Card className="p-8">
          <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center">
            <User className="w-5 h-5 mr-2 text-purple-600" />
            Create Your Account
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  First Name *
                </label>
                <Input
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  placeholder="John"
                  data-testid="author-first-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Last Name *
                </label>
                <Input
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  placeholder="Smith"
                  data-testid="author-last-name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address *
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                data-testid="author-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number (Optional)
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+44 7123 456789"
                data-testid="author-phone"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Lock className="w-4 h-4 inline mr-1" />
                Password *
              </label>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                placeholder="Minimum 6 characters"
                data-testid="author-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Lock className="w-4 h-4 inline mr-1" />
                Confirm Password *
              </label>
              <Input
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                required
                minLength={6}
                placeholder="Confirm your password"
                data-testid="author-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
              data-testid="author-register-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="spinner mr-2"></span>
                  Creating Account...
                </span>
              ) : (
                <>
                  <BookOpen className="w-5 h-5 mr-2" />
                  Create Account & Submit Book Details
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{' '}
            <a href="/author-login" className="text-purple-600 hover:underline">
              Login here
            </a>
          </p>
        </Card>
      </div>
    </div>
  );
};

export default AuthorOrderConfirmation;
